import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Edit2, Droplets, Lock, Check, Copy } from 'lucide-react';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';
import { DeviceTemplateProps, TraitStyleConfig, AttributeStyleConfig, IconStyleId } from '../../types';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';

// Import Attribute Components (fallback)
import { Style04_Circle as SensorCircle } from '../../attributes/SensorDisplay/Style04_Circle';

/**
 * Dehumidifier Template 02 - Modern UI
 * 
 * 使用的模組化組件：
 * - SensorDisplay/Style04_Circle (圓形濕度大顯示)
 * - OnOff/Style05_TextButton (電源文字按鈕)
 * - HumiditySetting/Style03_SliderMarks (目標濕度滑桿帶刻度)
 * - Mode/Style03_IconCircle (運轉模式圓形圖標按鈕)
 * - Timer/Style01_Grid (預約定時網格)
 * - FanSpeed/Style02_Pills (風速設定藥丸按鈕)
 * - CustomToggles/Style02_HorizontalSwitch (功能開關水平 Toggle)
 */
export const Template02_Modern: React.FC<DeviceTemplateProps> = ({
  device,
  isExecutingCommand,
  onTraitControl,
  onClose,
  onNicknameEdit,
  onNicknameSave,
  config,
  isMerged,
  endpointCount,
  onToggleMerge
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(device.nickname || device.deviceName);
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];
  // Unified order: Attributes + Traits (BigIcon is always fixed at top)
  const traitOrder: string[] = config?.traitOrder || ['SensorDisplay', 'OnOff', 'HumiditySetting', 'Mode', 'Timer', 'FanSpeed', 'CustomToggles'];

  // Sync tempName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && Date.now() > blockSyncUntilRef.current) {
      setTempName(device.nickname || device.deviceName);
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  const handleBlur = () => {
    if (!isSavingRef.current) {
      setIsEditingName(false);
    }
  };
  
  const handleSaveName = async () => {
    if (onNicknameSave && tempName.trim()) {
      isSavingRef.current = true;
      const savedName = tempName.trim();
      blockSyncUntilRef.current = Date.now() + 3000;
      await onNicknameSave(device.deviceId, savedName);
      setIsEditingName(false);
      isSavingRef.current = false;
    }
  };
  
  const traits = device.traits || [];
  const attributes = device.attributes || {};
  
  // Extract custom modes from device attributes (from API: availableCustomModes)
  const customModes = attributes.availableCustomModes || [];
  const modeConfig = customModes.find((m: any) => m.name === 'Mode');
  const fanSpeedConfig = customModes.find((m: any) => m.name === 'FanSpeed');
  const timerConfig = customModes.find((m: any) => m.name === 'Timer');
  
  // Extract toggles from device attributes (from API: availableCustomToggles)
  const customToggles = (attributes.availableCustomToggles || []).filter((t: string) => t !== 'Lock');
  const hasLock = (attributes.availableCustomToggles || []).includes('Lock');
  
  // Humidity range from device attributes (from API: humiditySetpointRange)
  const humidityRange = attributes.humiditySetpointRange || { minPercent: 40, maxPercent: 70 };
  
  // Generate marks dynamically based on actual min/max from API
  const generateMarks = (min: number, max: number, step: number = 10): number[] => {
    const marks: number[] = [];
    for (let i = min; i <= max; i += step) {
      marks.push(i);
    }
    return marks;
  };
  const humidityMarks = generateMarks(humidityRange.minPercent, humidityRange.maxPercent, 10);

  // Device power state - when OFF, disable all controls except power button
  const isDeviceOn = device.isOn ?? false;
  const isDeviceOff = !isDeviceOn;

  // Water tank full detection
  // bucket value: 0 = normal, 1 = full
  // notify array may contain { type: 'bucket', event: 'FallingCritical' } when full
  const bucketValue = (device as any).bucket?.[0] ?? (device as any).bucketValue ?? 0;
  const bucketNotify = ((device as any).notify || []).find((n: any) => n.type === 'bucket');
  const isWaterTankFull = bucketValue > 0 || bucketNotify?.event === 'FallingCritical' || bucketNotify?.event === 'Full';

  // Handlers
  const handleOnOff = () => {
    onTraitControl(device, 'OnOff', device.isOn ? 'off' : 'on');
  };

  const handleHumidityChange = (value: number) => {
    onTraitControl(device, 'HumiditySetting', 'setHumidity', { humiditySetpointPercent: value });
  };

  const handleModeChange = (modeName: string, value: string) => {
    onTraitControl(device, 'CustomModes', 'setModes', { 
      updateModeSettings: { [modeName]: value } 
    });
  };

  const handleToggleChange = (toggleName: string, value: boolean) => {
    onTraitControl(device, 'CustomToggles', 'setToggles', {
      updateToggleSettings: { [toggleName]: value }
    });
  };

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setTempName(device.nickname || device.deviceName);
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={handleBackdropClick}>
      <div className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="dehumidifier" 
              isOn={device.isOn} 
              size={20}
              styleId={smallIconStyle}
              className="flex-shrink-0"
            />
            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    }
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={handleBlur}
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveName}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsEditingName(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{device.nickname || device.deviceName}</h2>
                {onNicknameSave && (
                  <button 
                    onClick={() => {
                      setTempName(device.nickname || device.deviceName);
                      setIsEditingName(true);
                    }}
                    className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.status === 'online',
                label: device.status === 'online' ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>


        {/* Content */}
        <div className="px-5 pb-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          
          {/* Big Icon - Device Icon */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-4">
              <DeviceIcon
                deviceType="dehumidifier"
                isOn={device.isOn}
                size={64}
                styleId={bigIconStyle}
                className={device.isOn ? 'text-blue-400' : 'text-slate-500'}
              />
            </div>
          )}

          {/* Dynamically rendered components based on traitOrder (Attributes + Traits) */}
          {traitOrder.map((componentName) => {
            if (hiddenComponents.includes(componentName)) return null;
            
            switch (componentName) {
              // === ATTRIBUTES ===
              case 'SensorDisplay':
                return (
                  <DynamicAttribute
                    key="SensorDisplay"
                    attributeName="SensorDisplay"
                    styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                    componentProps={{
                      type: "humidity",
                      value: device.humidity ?? device.humidityAmbientPercent,
                      label: "目前濕度",
                      size: "large",
                      isWaterTankFull: isWaterTankFull,
                      className: isDeviceOff && !isWaterTankFull ? 'opacity-60' : ''
                    }}
                  />
                );
              
              // === TRAITS ===
              case 'OnOff':
                if (!traits.includes('OnOff')) return null;
                return (
                  <DynamicTrait
                    key="OnOff"
                    traitName="OnOff"
                    styleId={getTraitStyleId('OnOff', traitStyles)}
                    componentProps={{
                      isOn: device.isOn ?? false,
                      onToggle: handleOnOff,
                      disabled: isExecutingCommand,
                      loading: isExecutingCommand,
                      label: "電源"
                    }}
                  />
                );
              
              case 'HumiditySetting':
                if (!traits.includes('HumiditySetting')) return null;
                return (
                  <div key="HumiditySetting" className={isDeviceOff ? 'opacity-40' : ''}>
                    <DynamicTrait
                      traitName="HumiditySetting"
                      styleId={getTraitStyleId('HumiditySetting', traitStyles)}
                      componentProps={{
                        value: device.humiditySetpointPercent ?? 50,
                        min: humidityRange.minPercent,
                        max: humidityRange.maxPercent,
                        step: 5,
                        marks: humidityMarks,
                        onChange: handleHumidityChange,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        label: "目標濕度"
                      }}
                    />
                  </div>
                );
              
              case 'Mode':
                if (!traits.includes('Mode')) return null;
                return (
                  <div key="Mode" className={isDeviceOff ? 'opacity-40' : ''}>
                    <DynamicTrait
                      traitName="Mode"
                      styleId={getTraitStyleId('Mode', traitStyles)}
                      componentProps={{
                        value: isDeviceOff ? undefined : (device.currentModeSettings?.Mode ?? (modeConfig?.settings?.[0] as any)?.name ?? modeConfig?.settings?.[0]),
                        options: modeConfig?.settings || ['auto', 'continuous', 'laundry'],
                        onChange: (v: string) => handleModeChange('Mode', v),
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        label: "運轉模式",
                        labels: { '1': '節能', '3': '標準', '5': '強力', '8': '連續' }
                      }}
                    />
                  </div>
                );
              
              case 'Timer':
                if (!traits.includes('Timer')) return null;
                return (
                  <div key="Timer" className={isDeviceOff ? 'opacity-40' : ''}>
                    <DynamicTrait
                      traitName="Timer"
                      styleId={getTraitStyleId('Timer', traitStyles)}
                      componentProps={{
                        value: isDeviceOff ? undefined : (device.currentModeSettings?.Timer ?? (timerConfig?.settings?.[0] as any)?.name ?? timerConfig?.settings?.[0]),
                        options: timerConfig?.settings || ['0', '1', '2', '4', '8'],
                        onChange: (v: string) => handleModeChange('Timer', v),
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        label: "預約定時"
                      }}
                    />
                  </div>
                );
              
              case 'FanSpeed':
                if (!traits.includes('FanSpeed')) return null;
                return (
                  <div key="FanSpeed" className={isDeviceOff ? 'opacity-40' : ''}>
                    <DynamicTrait
                      traitName="FanSpeed"
                      styleId={getTraitStyleId('FanSpeed', traitStyles)}
                      componentProps={{
                        value: isDeviceOff ? undefined : (device.currentModeSettings?.FanSpeed ?? (fanSpeedConfig?.settings?.[0] as any)?.name ?? fanSpeedConfig?.settings?.[0]),
                        options: fanSpeedConfig?.settings || ['low', 'medium', 'high', 'auto'],
                        onChange: (v: string) => handleModeChange('FanSpeed', v),
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        label: "風速設定",
                        labels: { '1': '低速', '2': '中速', '3': '高速', '4': '強速', 'low': '低速', 'medium': '中速', 'high': '高速', 'auto': '自動' }
                      }}
                    />
                  </div>
                );
              
              case 'CustomToggles':
                if (customToggles.length === 0) return null;
                return (
                  <div key="CustomToggles" className={isDeviceOff ? 'opacity-40' : ''}>
                    <DynamicTrait
                      traitName="CustomToggles"
                      styleId={getTraitStyleId('CustomToggles', traitStyles)}
                      componentProps={{
                        toggles: customToggles,
                        values: isDeviceOff ? {} : (device.currentToggleSettings || {}),
                        onChange: handleToggleChange,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        columns: 2
                      }}
                    />
                  </div>
                );
              
              default:
                return null;
            }
          })}

          {/* Lock Button */}
          {/* 設備 OFF 時禁用 */}
          {hasLock && (
            <button
              onClick={() => handleToggleChange('Lock', !device.currentToggleSettings?.Lock)}
              disabled={isExecutingCommand || isDeviceOff}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-colors border border-slate-700/30 ${
                isDeviceOff 
                  ? 'bg-slate-800/20 text-slate-600 opacity-40 cursor-not-allowed' 
                  : 'bg-slate-800/40 text-slate-400 hover:bg-slate-700/50'
              }`}
            >
              <Lock className="w-4 h-4" />
              安全鎖
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Multi-endpoint device merge toggle - only for multi_channel devices */}
          {device.isMultiChannel && endpointCount && endpointCount > 1 && onToggleMerge && (
            <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
              <span className="text-xs text-slate-400">
                多端點設備 ({endpointCount} 個)
              </span>
              <button
                onClick={() => onToggleMerge(device.baseSN)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  isMerged 
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                    : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
              </button>
            </div>
          )}
          
          {/* Location & SN with copy */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{device.deviceId || device.baseSN}</span>
              <button
                onClick={() => navigator.clipboard.writeText(device.deviceId || device.baseSN)}
                className="text-slate-400 hover:text-white transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template02_Modern;
